import { SubscriptionTier } from '@wwe-portal/entities/subscription.type';

import {
  RosterLevelEnum,
  RosterLevelType,
} from '@wwe-portal/entities/roster.type';
import type { CurrentSubscriptionPlan } from '@wwe-portal/entities/subscription.type';
import { isBetterTier } from '../subscription/comparer';
import {
  cardStatusLabel,
  getCardStatus,
  offerToItemProps,
  parseRegionalOfferPrice,
} from '@wwe-portal/web/utils/offer/mapper';
import { FilterablePlayerRoster } from '@wwe-portal/web/components/club-store/type';
import {
  ExclusiveItemResponse,
  RosterItemResponse,
} from '@wwe-portal/entities/club-store.type';
import { LayoutTypeEnum, OfferCardProps } from '@wwe-portal/ui/features/offer';
import { ExclusiveItemProps } from '@wwe-portal/ui/features/club-store';
import { getEndDate } from '@wwe-portal/web/utils/offer';

export const offerToExclusiveOfferProps = (offer: ExclusiveItemResponse) => {
  const { isComingSoon, isNotReady } = getCardStatus(offer.offerData);
  const statusLabel = cardStatusLabel({
    isComingSoon,
    isNotReady,
  });
  const { price, priceNum } = parseRegionalOfferPrice(offer.offerData);

  if (typeof offer.offerData === 'string') {
    offer.offerData = JSON.parse(offer.offerData);
  }
  const props: OfferCardProps = {
    ...offer.offerData,
    id: offer.offerId,
    dynamicBackgroundImage: offer.offerData?.OfferBackgroundImage,
    backgroundImage: offer.offerData?.OfferBackgroundImage,
    title: offer.offerData?.StoreItemName,
    strikethroughPrice: offer.offerData?.StrikeThroughPrice
      ? String(offer.offerData.StrikeThroughPrice)
      : undefined,
    maxQuantity: offer.offerData.StockQuantity,
    quantity: offer.offerData.StockQuantity,
    type: offer.offerData.CombinationType as never,
    items: offerToItemProps(offer.offerData),
    endDate: getEndDate(offer.offerData.EndDate),
    price: statusLabel ?? price,
    priceNum,
    currentIconProps: undefined,
    layoutType: offer.offerData.LayoutType as LayoutTypeEnum,
    storeItemDescription: offer.offerData.StoreItemDescription,
    offerDescriptionText: offer.offerData.OfferDescriptionText,
    badgeText: offer.offerData.badgeText,
  };
  return props;
};

const validLevels = Object.values(RosterLevelEnum);
export const parseRosterItemToProps = ({
  solidStars,
  image,
  name,
  ID: id,
  level,
  rosterId,
  superstarId,
  tier,
  nickname,
  hollowStars,
  talent,
  title,
  class: _class,
  era,
  stable,
  e3,
}: RosterItemResponse) => {
  const props: FilterablePlayerRoster = {
    id,
    name: name ?? '-',
    star: solidStars,
    img: image,
    description: nickname,
    level: validLevels.includes(level as never)
      ? (level as RosterLevelType)
      : undefined,
    rosterId: rosterId ?? superstarId,
    tier,
    hollowStar: hollowStars ?? 0,
    title,
    class: _class,
    era,
    stable,
    price: talent,
    e3,
  };
  return props;
};

export const subscriptionMap = {
  [SubscriptionTier.BRONZE]: {
    name: 'Premium',
  },
  [SubscriptionTier.SILVER]: {
    name: 'Prestige',
  },
  [SubscriptionTier.GOLD]: {
    name: 'SuperVIP',
  },
};

export const subCategoryTypeToTier = (
  type: number | SubscriptionTier | undefined
) => {
  if (typeof type === 'number')
    return Object.values(SubscriptionTier)[type - 1];
  return type;
};

export const isBlockOffer = (
  type: number | SubscriptionTier | undefined,
  currentPlan: CurrentSubscriptionPlan | undefined
) => {
  if (!currentPlan) return false;

  return isBetterTier(
    {
      tier: subCategoryTypeToTier(type) ?? SubscriptionTier.BRONZE,
    },
    currentPlan
  );
};

export const parseExclusiveItemToProps = (
  rawOffer: ExclusiveItemResponse,
  currentPlan: CurrentSubscriptionPlan | undefined
) => {
  const props: ExclusiveItemProps & {
    isBlocked: boolean;
    tier: SubscriptionTier | undefined;
  } = {
    id: rawOffer.offerId,
    offerData: offerToExclusiveOfferProps(rawOffer),
    tier: Object.values(SubscriptionTier)[rawOffer.subCategoryType - 1],
    endDate: rawOffer.offerData.EndDate
      ? new Date(rawOffer.offerData.EndDate)
      : undefined,
    isBlocked: isBlockOffer(rawOffer.subCategoryType, currentPlan),
    moreInfoData: {
      detailsDescription: rawOffer.offerData?.StoreItemDescription ?? '',
      rewardList: rawOffer.offerData.OfferItemContent ?? [],
      actionBtn: undefined,
    },
  };

  return props;
};
